// WbCustomerDetailPage — /weighbridge/customers/:encId.
//
// Phase C3. Three sections:
//   A. Customer info (read-only, from the central customers row) + a
//      deep-link to the central customer edit page.
//   B. Weighbridge settings (editable) — PUT /wb/customer-settings/:encId.
//   C. Linked contracts + recent tickets for this customer (read-only).
//
// The URL param is the customer's enc_id (the WB settings table is keyed on
// customer_id). The settings GET embeds the customer's core fields so this
// page needs no separate central-customer fetch.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Badge } from '../../../components/ui/Badge/Badge';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useWbCustomerSettings,
  useUpdateWbCustomerSettings,
  type UpdateWbCustomerSettingsInput,
  type WbAccountStatus,
} from '../hooks/useWbCustomers';
import { useWbPriceBands } from '../hooks/useWbPriceBands';
import { useWbVehicles } from '../hooks/useWbVehicles';
import { useWbContracts, type ContractStatus } from '../hooks/useWbContracts';
import { useWbTickets, type TicketStatus } from '../hooks/useWbTickets';
import {
  WbHaulierSearch,
  type WbHaulierSearchValue,
} from '../components/WbHaulierSearch';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbCustomerDetailPage.module.css';

const ACCOUNT_STATUSES: ReadonlyArray<{ value: WbAccountStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

const EWC_REGEX = /^\d{6}$/;

interface FormState {
  account_status: WbAccountStatus;
  on_hold_reason: string;
  credit_limit: string; // text — empty === null
  payment_terms_days: string; // text — empty === null
  price_band_enc_id: string; // '' === none
  haulier: WbHaulierSearchValue | null;
  default_vehicle_enc_id: string; // '' === none
  default_ewc_code: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  account_status: 'active',
  on_hold_reason: '',
  credit_limit: '',
  payment_terms_days: '',
  price_band_enc_id: '',
  haulier: null,
  default_vehicle_enc_id: '',
  default_ewc_code: '',
  notes: '',
};

function formatCurrency(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
}

function ContractStatusBadge({ status }: { status: ContractStatus }): JSX.Element {
  switch (status) {
    case 'active':
      return <Badge variant="success">Active</Badge>;
    case 'draft':
      return <Badge variant="warning">Draft</Badge>;
    case 'expired':
    case 'cancelled':
    default:
      return <Badge variant="muted">{status}</Badge>;
  }
}

function TicketStatusBadge({ status }: { status: TicketStatus }): JSX.Element {
  switch (status) {
    case 'completed':
      return <Badge variant="success">Completed</Badge>;
    case 'voided':
      return <Badge variant="danger">Voided</Badge>;
    case 'open':
      return <Badge variant="warning">Open</Badge>;
    case 'draft':
    default:
      return <Badge variant="muted">Draft</Badge>;
  }
}

export function WbCustomerDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { encId } = useParams<{ encId: string }>();

  const settingsQuery = useWbCustomerSettings(encId);
  const updateMut = useUpdateWbCustomerSettings(encId ?? '');
  const priceBandsQuery = useWbPriceBands();
  const vehiclesQuery = useWbVehicles({ per_page: 100, is_active: true });
  const contractsQuery = useWbContracts(
    encId ? { customer_enc_id: encId } : {},
  );
  const ticketsQuery = useWbTickets(
    encId ? { customer_enc_id: encId, per_page: 5 } : {},
  );

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  const customer = settingsQuery.data?.customer;
  const settings = settingsQuery.data?.settings;

  // Hydrate the form whenever the settings GET resolves.
  useEffect(() => {
    if (!settings) return;
    setForm({
      account_status: settings.account_status,
      on_hold_reason: settings.on_hold_reason ?? '',
      credit_limit:
        settings.credit_limit !== null ? String(settings.credit_limit) : '',
      payment_terms_days:
        settings.payment_terms_days !== null
          ? String(settings.payment_terms_days)
          : '',
      price_band_enc_id: settings.price_band?.enc_id ?? '',
      haulier: settings.default_haulier
        ? {
            enc_id: settings.default_haulier.enc_id,
            name: settings.default_haulier.name,
          }
        : null,
      default_vehicle_enc_id: settings.default_vehicle?.enc_id ?? '',
      default_ewc_code: settings.default_ewc_code?.code ?? '',
      notes: settings.notes ?? '',
    });
    setDirty(false);
  }, [settings]);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (form.credit_limit.trim() !== '') {
      const n = Number(form.credit_limit);
      if (!Number.isFinite(n) || n < 0) {
        errs['credit_limit'] = 'Credit limit must be a non-negative number.';
      }
    }
    if (form.payment_terms_days.trim() !== '') {
      const n = Number(form.payment_terms_days);
      if (!Number.isInteger(n) || n < 0 || n > 65_535) {
        errs['payment_terms_days'] = 'Payment terms must be a whole number of days.';
      }
    }
    const ewc = form.default_ewc_code.trim();
    if (ewc !== '' && !EWC_REGEX.test(ewc)) {
      errs['default_ewc_code'] = 'EWC code must be exactly 6 digits.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildBody(): UpdateWbCustomerSettingsInput {
    const ewc = form.default_ewc_code.trim();
    const onHoldReason = form.on_hold_reason.trim();
    const notes = form.notes.trim();
    return {
      account_status: form.account_status,
      // Clear the reason whenever the account isn't on hold.
      on_hold_reason:
        form.account_status === 'on_hold'
          ? onHoldReason === ''
            ? null
            : onHoldReason
          : null,
      credit_limit:
        form.credit_limit.trim() === '' ? null : Number(form.credit_limit),
      payment_terms_days:
        form.payment_terms_days.trim() === ''
          ? null
          : Number(form.payment_terms_days),
      price_band_id: form.price_band_enc_id === '' ? null : form.price_band_enc_id,
      default_haulier_id: form.haulier?.enc_id ?? null,
      default_vehicle_id:
        form.default_vehicle_enc_id === '' ? null : form.default_vehicle_enc_id,
      default_ewc_code: ewc === '' ? null : ewc,
      notes: notes === '' ? null : notes,
    };
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    updateMut.mutate(buildBody(), {
      onSuccess: () => {
        setDirty(false);
        navigate('/weighbridge/customers');
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  function handleLeave(): void {
    if (dirty) {
      setConfirmLeaveOpen(true);
      return;
    }
    navigate('/weighbridge/customers');
  }

  const priceBands = priceBandsQuery.data?.price_bands ?? [];
  const vehicles = vehiclesQuery.data?.vehicles ?? [];
  const contracts = contractsQuery.data?.contracts ?? [];
  const recentTickets = ticketsQuery.data?.tickets ?? [];

  const allTicketsLink = useMemo(
    () =>
      encId
        ? `/weighbridge/tickets?customer_enc_id=${encodeURIComponent(encId)}`
        : '/weighbridge/tickets',
    [encId],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={customer?.name ?? 'Customer'}
        subtitle="Weighbridge account settings"
        actions={
          <Button variant="secondary" icon={ArrowLeft} onClick={handleLeave}>
            Back to Customers
          </Button>
        }
      />

      {settingsQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(settingsQuery.error)}
        </p>
      ) : null}

      {settingsQuery.isLoading || !settings || !customer ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading customer" />
          </div>
        </Card>
      ) : (
        <>
          {/* Section A — customer info (read-only) */}
          <Card>
            <h3 className={styles.sectionHeading}>Customer details</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Name</span>
                <span className={styles.infoValue}>{customer.name}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Email</span>
                <span className={styles.infoValue}>
                  {customer.email ?? <span className={styles.muted}>—</span>}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Phone</span>
                <span className={styles.infoValue}>
                  {customer.phone ?? <span className={styles.muted}>—</span>}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Address</span>
                <span className={styles.infoValue}>
                  {customer.address ?? <span className={styles.muted}>—</span>}
                </span>
              </div>
            </div>
            <div className={styles.sectionLink}>
              <Button
                variant="ghost"
                size="sm"
                icon={ExternalLink}
                onClick={() => navigate(`/customers/${customer.uuid}/edit`)}
              >
                Edit customer details
              </Button>
            </div>
          </Card>

          {/* Section B — weighbridge settings (editable) */}
          <form onSubmit={handleSubmit} className={styles.form}>
            <Card>
              <h3 className={styles.sectionHeading}>Weighbridge settings</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Account status</span>
                  <select
                    value={form.account_status}
                    onChange={(e) =>
                      patch('account_status', e.target.value as WbAccountStatus)
                    }
                  >
                    {ACCOUNT_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>

                {form.account_status === 'on_hold' ? (
                  <label className={styles.field}>
                    <span>On-hold reason</span>
                    <input
                      type="text"
                      maxLength={500}
                      value={form.on_hold_reason}
                      onChange={(e) => patch('on_hold_reason', e.target.value)}
                      placeholder="Why is this account on hold?"
                    />
                  </label>
                ) : null}

                <label className={styles.field}>
                  <span>Credit limit (£)</span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={form.credit_limit}
                    onChange={(e) => patch('credit_limit', e.target.value)}
                    placeholder="No limit"
                  />
                  {fieldErrors['credit_limit'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['credit_limit']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Payment terms (days)</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.payment_terms_days}
                    onChange={(e) => patch('payment_terms_days', e.target.value)}
                    placeholder="Optional"
                  />
                  <span className={styles.fieldHelp}>
                    Net payment days for account customers.
                  </span>
                  {fieldErrors['payment_terms_days'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['payment_terms_days']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Price band</span>
                  <select
                    value={form.price_band_enc_id}
                    onChange={(e) => patch('price_band_enc_id', e.target.value)}
                  >
                    <option value="">— None —</option>
                    {priceBands.map((pb) => (
                      <option key={pb.enc_id} value={pb.enc_id}>
                        {pb.name} ({pb.code})
                      </option>
                    ))}
                  </select>
                </label>

                <label className={styles.field}>
                  <span>Default haulier</span>
                  <WbHaulierSearch
                    value={form.haulier}
                    onChange={(next) => patch('haulier', next)}
                  />
                </label>

                <label className={styles.field}>
                  <span>Default vehicle</span>
                  <select
                    value={form.default_vehicle_enc_id}
                    onChange={(e) =>
                      patch('default_vehicle_enc_id', e.target.value)
                    }
                  >
                    <option value="">— None —</option>
                    {vehicles.map((v) => (
                      <option key={v.enc_id} value={v.enc_id}>
                        {v.registration}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={styles.field}>
                  <span>Default EWC code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={form.default_ewc_code}
                    onChange={(e) => patch('default_ewc_code', e.target.value)}
                    placeholder="e.g. 170101"
                  />
                  <span className={styles.fieldHelp}>
                    Prefilled on this customer's tickets. 6 digits, no spaces.
                  </span>
                  {fieldErrors['default_ewc_code'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['default_ewc_code']}
                    </span>
                  ) : null}
                </label>
              </div>

              <label className={styles.field} style={{ marginTop: '1rem' }}>
                <span>Notes</span>
                <textarea
                  rows={3}
                  maxLength={5000}
                  value={form.notes}
                  onChange={(e) => patch('notes', e.target.value)}
                  placeholder="Optional"
                />
              </label>
            </Card>

            {error ? <p className={styles.error}>{error}</p> : null}

            <div className={styles.actions}>
              <div className={styles.rightActions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleLeave}
                  disabled={updateMut.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={updateMut.isPending}
                  disabled={!dirty}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </form>

          {/* Section C — contracts + recent tickets (read-only) */}
          <Card>
            <h3 className={styles.sectionHeading}>Contracts</h3>
            {contractsQuery.isLoading ? (
              <div className={styles.emptyMini}>Loading contracts…</div>
            ) : contracts.length === 0 ? (
              <div className={styles.emptyMini}>
                No contracts for this customer.
              </div>
            ) : (
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Valid</th>
                    <th>Lines</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr
                      key={c.enc_id}
                      className={styles.clickRow}
                      onClick={() =>
                        navigate(`/weighbridge/pricing/contracts/${c.enc_id}`)
                      }
                    >
                      <td>{c.name}</td>
                      <td>
                        {formatDate(c.valid_from)} – {formatDate(c.valid_to)}
                      </td>
                      <td>{c.line_count}</td>
                      <td>
                        <ContractStatusBadge status={c.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card>
            <h3 className={styles.sectionHeading}>Recent tickets</h3>
            {ticketsQuery.isLoading ? (
              <div className={styles.emptyMini}>Loading tickets…</div>
            ) : recentTickets.length === 0 ? (
              <div className={styles.emptyMini}>
                No tickets for this customer yet.
              </div>
            ) : (
              <>
                <table className={styles.miniTable}>
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Date</th>
                      <th className={styles.numeric}>Net (kg)</th>
                      <th className={styles.numeric}>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTickets.map((t) => (
                      <tr
                        key={t.enc_id}
                        className={styles.clickRow}
                        onClick={() =>
                          navigate(`/weighbridge/tickets/${t.enc_id}`)
                        }
                      >
                        <td>{t.ticket_number ?? '—'}</td>
                        <td>{formatDate(t.created_at)}</td>
                        <td className={styles.numeric}>
                          {t.net_weight_kg !== null
                            ? t.net_weight_kg.toLocaleString()
                            : '—'}
                        </td>
                        <td className={styles.numeric}>
                          {formatCurrency(t.total)}
                        </td>
                        <td>
                          <TicketStatusBadge status={t.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className={styles.sectionLink}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(allTicketsLink)}
                  >
                    View all tickets
                  </Button>
                </div>
              </>
            )}
          </Card>
        </>
      )}

      <ConfirmModal
        open={confirmLeaveOpen}
        title="Discard unsaved changes?"
        message={<p>You have unsaved changes. Leave without saving?</p>}
        variant="danger"
        confirmLabel="Discard"
        onConfirm={() => {
          setConfirmLeaveOpen(false);
          navigate('/weighbridge/customers');
        }}
        onCancel={() => setConfirmLeaveOpen(false)}
      />
    </div>
  );
}
